import { ByteReader, ByteWriter, decodeResponse, encodeRequest } from './timbusEnvelope'
import { fetchWorkerCommand } from './workerApi'

/**
 * The shipping app sends 0x02 here. Probed live: 0x01, 0x02 and 0x03 all return
 * byte-identical responses, so this byte is inert — it is not a mode, a transfer
 * limit or a result cap. Hardcoded rather than exposed, because offering a
 * control that provably does nothing is worse than offering none.
 */
const OPTION_FLAG = 0x02

export interface PlannerPoint {
  lat: number
  lng: number
}

export interface ItineraryEndpoint extends PlannerPoint {
  /** Stop name as the service reports it. */
  name: string
}

export interface ItineraryLeg {
  /** Resolves against `BusRoute.id` in routes.json. Confirmed. */
  routeId: number
  /**
   * 0-based positions along the route's stop list. Confirmed by relationship:
   * `(alight - board) × ~130 m` tracks `distanceMeters` across every observed
   * leg, from a 3-stop hop to a 110-stop ride.
   */
  boardStopIndex: number
  alightStopIndex: number
  /** Ride distance for THIS leg in metres. Confirmed. */
  distanceMeters: number
  /**
   * Ride duration in seconds — **only trustworthy on single-leg itineraries.**
   *
   * On every observed 2-leg plan exactly one leg carries a duration and the
   * other reads 0, so this is not a reliable per-leg figure and the two must not
   * simply be summed and presented as a journey time. Use `totalDurationSeconds`
   * on the itinerary, which is explicit about the same caveat.
   */
  durationSeconds: number
  /**
   * Decoded solely to keep the byte stream aligned — skipping a field corrupts
   * everything after it. Meanings UNKNOWN, so they must not reach the UI.
   *
   * `leading` was briefly labelled "walk distance to the boarding stop": it is
   * constant for a fixed first boarding stop, which fits, but reads 4904 on a
   * transfer leg, and nobody walks 4.9 km between buses. Demoted back to unknown.
   */
  unknown: {
    leading: number
    afterRouteId: number
    trailingFlag: number
  }
}

export interface Itinerary {
  board: ItineraryEndpoint
  alight: ItineraryEndpoint
  legs: ItineraryLeg[]
  /** Sum across legs. Reliable — per-leg distances are all populated. */
  totalDistanceMeters: number
  /**
   * Sum across legs, but see `ItineraryLeg.durationSeconds`: on a multi-leg plan
   * one leg reports 0, so this UNDERSTATES a journey with a transfer. Treat it as
   * a lower bound and say so wherever it is displayed.
   */
  totalDurationSeconds: number
  /** True when any leg reported no duration, i.e. the total is a lower bound. */
  durationIsPartial: boolean
}

export function buildRouteOptionsRequest(start: PlannerPoint, end: PlannerPoint) {
  const writer = new ByteWriter()
  // Request order is (lat, lng) — note the RESPONSE comes back longitude-first.
  writer.float(start.lat)
  writer.float(start.lng)
  writer.float(end.lat)
  writer.float(end.lng)
  writer.byte(OPTION_FLAG)
  return writer.build()
}

/**
 * Parses a RouteBuytOptionV3 payload.
 *
 * Field map established by probing the live service — see the task's
 * `implement.md` for the evidence. Two traps encoded here:
 *
 * 1. **Coordinates come back longitude-first**, the opposite order to the
 *    request. Reading them in request order puts every itinerary in the Indian
 *    Ocean.
 * 2. A pair with no service returns a short run of ASCII `'0'` padding rather
 *    than a count-led list. That is "no itinerary", not a protocol error.
 */
export function parseRouteOptions(payload: Uint8Array): Itinerary[] {
  if (payload.length === 0) return []

  const reader = new ByteReader(payload)
  const count = reader.unsignedByte()

  // The padding response decodes to a count of 0x30 ('0') with nothing usable
  // behind it. Guard on the payload being long enough for what it claims.
  if (count === 0) return []

  const itineraries: Itinerary[] = []

  try {
    for (let index = 0; index < count; index += 1) {
      const board = readEndpoint(reader)
      const legCount = reader.unsignedByte()
      const legs: ItineraryLeg[] = []

      for (let leg = 0; leg < legCount; leg += 1) {
        const leading = reader.int()
        // Each index is written twice in a row; the duplicate carries no extra
        // information in any response observed.
        const boardStopIndex = reader.short()
        reader.short()
        const hasRide = reader.unsignedByte()

        // When clear, the route block that follows is absent and the leg
        // carries no ride.
        if (!hasRide) continue

        const routeId = reader.int()
        const afterRouteId = reader.int()
        const alightStopIndex = reader.short()
        reader.short()
        const distanceMeters = reader.int()
        const durationSeconds = reader.int()
        const trailingFlag = reader.unsignedByte()

        legs.push({
          routeId,
          boardStopIndex,
          alightStopIndex,
          distanceMeters,
          durationSeconds,
          unknown: { leading, afterRouteId, trailingFlag },
        })
      }

      const alight = readEndpoint(reader)
      itineraries.push({
        board,
        alight,
        legs,
        totalDistanceMeters: legs.reduce((sum, leg) => sum + leg.distanceMeters, 0),
        totalDurationSeconds: legs.reduce((sum, leg) => sum + leg.durationSeconds, 0),
        durationIsPartial: legs.some((leg) => leg.durationSeconds === 0),
      })
    }
  } catch {
    // A truncated or non-itinerary payload yields whatever parsed cleanly rather
    // than throwing — the caller cannot act on a half-read buffer anyway, and an
    // unserved pair is a normal outcome, not an error.
    return itineraries
  }

  return itineraries
}

function readEndpoint(reader: ByteReader): ItineraryEndpoint {
  // Longitude first. See the class comment.
  const lng = reader.float()
  const lat = reader.float()
  return { lat, lng, name: reader.string(2) }
}

interface FetchRouteOptionsOptions {
  signal?: AbortSignal
  session?: string
}

export async function fetchRouteOptions(
  start: PlannerPoint,
  end: PlannerPoint,
  { signal, session }: FetchRouteOptionsOptions = {},
): Promise<Itinerary[]> {
  const requestParameter = encodeRequest(buildRouteOptionsRequest(start, end))

  const signals = [AbortSignal.timeout(20_000)]
  if (signal) signals.push(signal)
  const response = await fetchWorkerCommand(
    `RouteBuytOptionV3?ReqParams=${encodeURIComponent(requestParameter)}`,
    { session, signal: AbortSignal.any(signals) },
  )
  if (!response.ok) throw new Error(`RouteBuytOptionV3 returned HTTP ${response.status}`)

  return parseRouteOptions(decodeResponse(await response.json()))
}
